import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, ExternalLink, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useXP } from '@/hooks/useXP';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { celebratePoints } from '@/utils/confetti';
import { ResponsiveImage } from '@/components/ResponsiveImage';

interface FeaturedCrossingAdProps {
  ad: {
    id: string;
    company_name: string;
    offer_text: string;
    image_url?: string;
    cta_url?: string;
    category?: string;
  };
  onDismiss?: () => void;
}

export const FeaturedCrossingAd = ({ ad, onDismiss }: FeaturedCrossingAdProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { addXP } = useXP();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState(false);

  const handleClick = async () => {
    try {
      // Award +3 XP
      await addXP(3);
      celebratePoints(3);

      // Track view and click via rate-limited edge function
      await supabase.functions.invoke('submit-ad-view', {
        body: {
          ad_id: ad.id,
          placement: 'featured_crossing',
          clicked: true,
        },
      });

      // Show toast
      toast({
        title: "🎉 " + (t('earnedXP') || '+3 XP earned!'),
        description: t('thanksForExploring') || "Thanks for checking this out!",
      });

      // Open URL
      if (ad.cta_url) {
        window.open(ad.cta_url, '_blank');
      }
    } catch (error) {
      console.error('Error tracking ad click:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in sessionStorage (7-day cooldown would be implemented server-side)
    sessionStorage.setItem(`ad_dismissed_${ad.id}`, Date.now().toString());
    onDismiss?.();
  };

  if (dismissed) return null;

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5 hover:shadow-lg transition-all">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Badge variant="secondary" className="flex items-center gap-1 text-xs">
            💡 {t('sponsored') || 'Sponsored'}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-6 w-6 p-0 hover:bg-destructive/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex gap-3">
          {ad.image_url && (
            <ResponsiveImage
              src={ad.image_url}
              alt={ad.company_name}
              width={64}
              height={64}
              className="w-16 h-16 rounded-lg"
              sizes="64px"
            />
          )}
          
          <div className="flex-1">
            <h4 className="font-semibold text-lg mb-1">{ad.company_name}</h4>
            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
              {ad.offer_text}
            </p>
          </div>
        </div>

        <Button 
          onClick={handleClick}
          className="w-full mt-3"
          variant="default"
        >
          {t('tapToLearnMore') || 'Learn More'} • +3 XP
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>

        <p className="text-xs text-center text-muted-foreground mt-2">
          {t('sponsoredBy') || 'Sponsored by'} {ad.company_name}
        </p>
      </CardContent>
    </Card>
  );
};
